import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../context/alert';
import { useTranslations } from '../../../hooks/useTranslations';
import { HelpResponse, CreateHelpRequest, UpdateHelpRequest } from '../../../types/api/help';
import { createHelp, updateHelp } from '../../../services/help';
import { fetchLanguages } from "../../../services/lang";
import ModalSheet from '../../basic/ModalSheet';
import LangModal from '../../categories/components/LangModal';
import TableActionsBanner from '../../common/TableActionsBanner';
import { LanguageResponse } from '../../../types/api/lang';
import { Stack, Button, Textarea, Text, Spinner } from '@primer/react'

interface HelpModalProps {
    faq: HelpResponse | undefined
    isOpen: boolean;
    onDidSaved: () => void;
    onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ faq, isOpen, onDidSaved, onClose }) => {

    const [allLangs, setAllLangs] = useState<LanguageResponse[]>([]);
    const [isPending, setIsPending] = useState<boolean>(false)
    const [openLangModal, setLangModal] = React.useState(false)
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);
    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const {
        translations,
        setTranslations,
        langs,
        setLangs,
        currentLangIndex,
        setCurrentLangIndex,
        getCurrentFieldValue,
        getRussianOrFirstFieldValue,
        updateFieldValue,
        isAllLanguagesFilled,
        langsDidUpdated,
        hasTranslationChanges
    } = useTranslations()

    const { setError, setInfo, addCustomAlert, updateAlert, removeAlert } = useAlert();

    const { mutate: fetchLanguagesRequest, isPending: isLoadingLang } = useMutation({
        mutationFn: fetchLanguages,
        onSuccess: (data) => {
            setAllLangs(data.list);
        },
        onError: (error: any) => {
            setAllLangs([]);
        }
    });

    const { mutate: createFaqRequest } = useMutation({
        mutationFn: (data: CreateHelpRequest) => createHelp(data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            handleClose()

            setInfo('Вопрос сохранен')
        },
        onError: (_) => setIsPending(false)
    });

    const { mutate: editFaqRequest } = useMutation({
        mutationFn: (data: UpdateHelpRequest) => updateHelp(faq?.id ?? '', data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            handleClose()

            setInfo('Изменения сохранены')
        },
        onError: (_) => setIsPending(false)
    });

    const saveFaq = () => {
        setIsPending(true)
        createFaqRequest({
            topicId: undefined,
            title: getRussianOrFirstFieldValue('title'),
            simpleText: getRussianOrFirstFieldValue('body'),
            html: undefined,
            markdown: undefined,
            translations: translations
        })
    }

    const updateFaq = () => {
        setIsPending(true)
        editFaqRequest({
            topicId: undefined,
            title: getRussianOrFirstFieldValue('title'),
            simpleText: getRussianOrFirstFieldValue('body'),
            html: undefined,
            markdown: undefined,
            translations: translations
        })
    }

    const trySaveFaq = () => {
        if (translations.length === 0) {
            setError('Вопросы заполнены не для всех выбранных языков')
            return
        }
        if (!isAllLanguagesFilled('title') || !isAllLanguagesFilled('body')) {
            setError('Вопросы заполнены не для всех выбранных языков')
            return
        }

        if (faq) {
            // Update
            updateFaq()
        } else {
            // Create
            saveFaq()
        }
    };

    useEffect(() => {
        setTranslations(faq?.translations ?? [])
        fetchLanguagesRequest({})
    }, [faq]);

    useEffect(() => {
        if (faq && allLangs) {
            // Если есть translations, используем их
            if (faq.translations && faq.translations.length > 0) {
                const faqLangCodes = faq.translations.map(el => el.languageIsoCode);
                const selectedLangs = allLangs.filter(lang => faqLangCodes.includes(lang.isoCode));
                setLangs(selectedLangs);
                setTranslations(faq.translations);
            }
        } else if (allLangs) {
            const russianLang = allLangs.find(lang => lang.isoCode === 'ru');
            if (russianLang) {
                setLangs([russianLang]);
                setTranslations([{
                    languageIsoCode: 'ru',
                    fields: [{ fieldName: 'title', value: "" }]
                }]);
                setCurrentLangIndex(0);
            }
        }
    }, [allLangs, faq]);

    const showConfirmDismissAlertOrExit = useCallback(() => {
        let hasChanges = false

        if (faq) {
            hasChanges = hasTranslationChanges(faq.translations);
        } else {
            hasChanges = translations.some(t =>
                t.fields.some(f => f.value.trim() !== "")
            );
        }

        if (hasChanges) {
            handleConfirmDismissAlert()
        } else {
            handleClose()
        }
    }, [actionsAlertId, translations, faq]);

    const handleConfirmDismissAlert = useCallback(() => {
        let props: any = undefined
        props = createConfirmDismissAlertProps()

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );

            setActionsAlertId(alertId)
        }
    }, [actionsAlertId]);

    function createConfirmDismissAlertProps() {
        const title = "Действительно хотите отменить изменения?"

        const buttons = [
            <Button key="clear-button"
                variant="default"
                onClick={dismissTableActionAlert}>
                Нет
            </Button>,
            <Button key="clear-button"
                variant="default"
                onClick={() => {
                    dismissTableActionAlert()
                    handleClose()
                }}>
                Да, отменить
            </Button>
        ]

        return {
            count: undefined,
            title: title,
            buttons: buttons,
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
        }
    }

    const resetState = useCallback(() => {
        setTranslations([]);
        setLangs([]);
        setCurrentLangIndex(0);
    }, [faq]);

    const handleClose = useCallback(() => {
        resetState(); // сбросить к исходному состоянию
        onClose();
    }, [resetState, onClose]);

    const title = faq === undefined ? 'Добавить вопрос' : 'Редактировать вопрос'
    const saveButtonTitle = faq === undefined ? 'Сохранить' : 'Сохранить изменения'

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={showConfirmDismissAlertOrExit}
            title={title}
            size="middle"
            headerSize="regular"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <Stack direction="horizontal" gap='condensed' justify='start'>
                        {langs.map((lang, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentLangIndex(index)}
                                className={`flex items-center gap-[4px] px-2 py-[6px] border-[1px] 
                        ${currentLangIndex === index ? "bg-aloix-black hover:bg-gray-800" : "bg-white hover:bg-gray-50 border-border"}
                         rounded-md  h-8`}>
                                <span className={`${currentLangIndex === index ? "text-white" : "text-aloix-black"} font-medium text-xs`}>{lang.titleRu || lang.titleOrigin}</span>
                            </button>
                        ))}
                        <LangModal
                            filterOpened={openLangModal}
                            setFilterOpened={(val) => setLangModal(val)}
                            selectedLangs={langs}
                            setSelectedFilter={langsDidUpdated}
                            onApply={() => { }}
                            onDismiss={() => { }}
                        />
                    </Stack>
                    {isLoadingLang && <Spinner />}
                    {langs.length > 0 &&
                        <>
                            <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                                Вопрос
                            </Text>
                            <Textarea
                                inputMode="text"
                                value={getCurrentFieldValue('title')}
                                onChange={(e) => updateFieldValue('title', e.target.value)}
                                maxLength={500}
                                rows={3}
                                resize="none"
                                placeholder="Напишите вопрос"
                                contrast={true}
                            />

                            <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                                Ответ
                            </Text>
                            <Textarea
                                inputMode="text"
                                value={getCurrentFieldValue('body')}
                                onChange={(e) => updateFieldValue('body', e.target.value)}
                                maxLength={1000}
                                rows={6}
                                resize="none"
                                placeholder="Напишите ответ к вопросу"
                                contrast={true}
                            />
                        </>
                    }

                    <Stack direction="horizontal" gap='normal' justify='end'>
                        <div></div>
                        <Button
                            onClick={showConfirmDismissAlertOrExit}
                            size='small'
                            variant="default">
                            Отмена
                        </Button>
                        <Button
                            onClick={trySaveFaq}
                            size='small'
                            variant="primary"
                            loading={isPending}
                            disabled={isPending ||
                                translations.length === 0 || (langs.length === 1 &&
                                    (
                                        !isAllLanguagesFilled('title') ||
                                        !isAllLanguagesFilled('body')
                                    )
                                )}
                        >
                            {saveButtonTitle}
                        </Button>
                    </Stack>
                </Stack>
            )}
        </ModalSheet>
    );
};